import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { pluckData } from 'src/utilities/http-client'
import type { RootState } from 'src/store/root-state'
import type { Status } from 'src/store/store'
import type {
  MotherSessionSummary,
  MotherSessionDetail,
  ExecutionGraph,
  MotherProgressEvent,
  MotherLoreFile,
  NodeDetail,
} from './types'

// API Endpoints
export let fetchMotherSessions = createAsyncThunk(
  'mother/fetchSessions',
  ({ page = 1, pageSize = 50 }: { page?: number; pageSize?: number } = {}) =>
    axios
      .get<MotherSessionSummary[]>('/api/v2/admin/mother/sessions', {
        params: { page, pageSize },
      })
      .then(pluckData),
)

export let fetchMotherSessionDetail = createAsyncThunk(
  'mother/fetchSessionDetail',
  (correlationID: string) =>
    axios
      .get<MotherSessionDetail>(
        `/api/v2/admin/mother/sessions/${correlationID}`,
      )
      .then(pluckData),
)

export let fetchMotherSessionGraph = createAsyncThunk(
  'mother/fetchSessionGraph',
  (correlationID: string) =>
    axios
      .get<ExecutionGraph>(
        `/api/v2/admin/mother/sessions/${correlationID}/graph`,
      )
      .then(pluckData),
)

export let fetchMotherSessionTimeline = createAsyncThunk(
  'mother/fetchSessionTimeline',
  (correlationID: string) =>
    axios
      .get<MotherProgressEvent[]>(
        `/api/v2/admin/mother/sessions/${correlationID}/timeline`,
      )
      .then(pluckData),
)

export let fetchMotherSessionLore = createAsyncThunk(
  'mother/fetchSessionLore',
  (correlationID: string) =>
    axios
      .get<MotherLoreFile[]>(
        `/api/v2/admin/mother/sessions/${correlationID}/lore`,
      )
      .then(pluckData),
)

export let fetchMotherNodeDetail = createAsyncThunk(
  'mother/fetchNodeDetail',
  ({ correlationID, nodeID }: { correlationID: string; nodeID: string }) =>
    axios
      .get<NodeDetail>(
        `/api/v2/admin/mother/sessions/${correlationID}/nodes/${nodeID}`,
      )
      .then(pluckData),
)

// State
type MotherState = {
  sessions: MotherSessionSummary[]
  sessionDetails: Record<string, MotherSessionDetail>
  sessionGraphs: Record<string, ExecutionGraph>
  sessionTimelines: Record<string, MotherProgressEvent[]>
  sessionLore: Record<string, MotherLoreFile[]>
  nodeDetails: Record<string, NodeDetail> // Key format: "correlationID:nodeID"
  sessionsStatus: Status
  sessionDetailStatus: Record<string, Status>
  sessionGraphStatus: Record<string, Status>
  sessionTimelineStatus: Record<string, Status>
  sessionLoreStatus: Record<string, Status>
  nodeDetailStatus: Record<string, Status> // Key format: "correlationID:nodeID"
  filters: {
    status: string | null
    search: string
    dateRange: { start: string | null; end: string | null }
  }
}

export let initialState: MotherState = {
  sessions: [],
  sessionDetails: {},
  sessionGraphs: {},
  sessionTimelines: {},
  sessionLore: {},
  nodeDetails: {},
  sessionsStatus: 'uninitialized',
  sessionDetailStatus: {},
  sessionGraphStatus: {},
  sessionTimelineStatus: {},
  sessionLoreStatus: {},
  nodeDetailStatus: {},
  filters: {
    status: null,
    search: '',
    dateRange: { start: null, end: null },
  },
}

// Slice
let motherSlice = createSlice({
  name: 'mother',
  initialState,
  reducers: {
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload
    },
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload
    },
    setDateRangeFilter: (state, action) => {
      state.filters.dateRange = action.payload
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
        search: '',
        dateRange: { start: null, end: null },
      }
    },
  },
  extraReducers: (builder) => {
    // Sessions list
    builder
      .addCase(fetchMotherSessions.pending, (state) => {
        state.sessionsStatus = 'pending'
      })
      .addCase(fetchMotherSessions.fulfilled, (state, action) => {
        state.sessionsStatus = 'fulfilled'
        state.sessions = action.payload
      })
      .addCase(fetchMotherSessions.rejected, (state) => {
        state.sessionsStatus = 'rejected'
      })

    // Session details
    builder
      .addCase(fetchMotherSessionDetail.pending, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionDetailStatus[correlationID] = 'pending'
      })
      .addCase(fetchMotherSessionDetail.fulfilled, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionDetailStatus[correlationID] = 'fulfilled'
        state.sessionDetails[correlationID] = action.payload
      })
      .addCase(fetchMotherSessionDetail.rejected, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionDetailStatus[correlationID] = 'rejected'
      })

    // Session graphs
    builder
      .addCase(fetchMotherSessionGraph.pending, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionGraphStatus[correlationID] = 'pending'
      })
      .addCase(fetchMotherSessionGraph.fulfilled, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionGraphStatus[correlationID] = 'fulfilled'
        state.sessionGraphs[correlationID] = action.payload
      })
      .addCase(fetchMotherSessionGraph.rejected, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionGraphStatus[correlationID] = 'rejected'
      })

    // Session timelines
    builder
      .addCase(fetchMotherSessionTimeline.pending, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionTimelineStatus[correlationID] = 'pending'
      })
      .addCase(fetchMotherSessionTimeline.fulfilled, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionTimelineStatus[correlationID] = 'fulfilled'
        state.sessionTimelines[correlationID] = action.payload
      })
      .addCase(fetchMotherSessionTimeline.rejected, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionTimelineStatus[correlationID] = 'rejected'
      })

    // Session lore
    builder
      .addCase(fetchMotherSessionLore.pending, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionLoreStatus[correlationID] = 'pending'
      })
      .addCase(fetchMotherSessionLore.fulfilled, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionLoreStatus[correlationID] = 'fulfilled'
        state.sessionLore[correlationID] = action.payload
      })
      .addCase(fetchMotherSessionLore.rejected, (state, action) => {
        let correlationID = action.meta.arg
        state.sessionLoreStatus[correlationID] = 'rejected'
      })

    // Node details
    builder
      .addCase(fetchMotherNodeDetail.pending, (state, action) => {
        let { correlationID, nodeID } = action.meta.arg
        let key = `${correlationID}:${nodeID}`
        state.nodeDetailStatus[key] = 'pending'
      })
      .addCase(fetchMotherNodeDetail.fulfilled, (state, action) => {
        let { correlationID, nodeID } = action.meta.arg
        let key = `${correlationID}:${nodeID}`
        state.nodeDetailStatus[key] = 'fulfilled'
        state.nodeDetails[key] = action.payload
      })
      .addCase(fetchMotherNodeDetail.rejected, (state, action) => {
        let { correlationID, nodeID } = action.meta.arg
        let key = `${correlationID}:${nodeID}`
        state.nodeDetailStatus[key] = 'rejected'
      })
  },
})

// Actions
export let {
  setStatusFilter,
  setSearchFilter,
  setDateRangeFilter,
  clearFilters,
} = motherSlice.actions

// Selectors
export let selectMotherSessions = (state: RootState) => state.mother.sessions
export let selectMotherSessionsStatus = (state: RootState) =>
  state.mother.sessionsStatus
export let selectMotherFilters = (state: RootState) => state.mother.filters

export let selectMotherSessionDetail =
  (correlationID: string) => (state: RootState) =>
    state.mother.sessionDetails[correlationID]

export let selectMotherSessionDetailStatus =
  (correlationID: string) => (state: RootState) =>
    state.mother.sessionDetailStatus[correlationID] || 'uninitialized'

export let selectMotherSessionGraph =
  (correlationID: string) => (state: RootState) =>
    state.mother.sessionGraphs[correlationID]

export let selectMotherSessionGraphStatus =
  (correlationID: string) => (state: RootState) =>
    state.mother.sessionGraphStatus[correlationID] || 'uninitialized'

export let selectMotherSessionTimeline =
  (correlationID: string) => (state: RootState) =>
    state.mother.sessionTimelines[correlationID]

export let selectMotherSessionTimelineStatus =
  (correlationID: string) => (state: RootState) =>
    state.mother.sessionTimelineStatus[correlationID] || 'uninitialized'

export let selectMotherSessionLore =
  (correlationID: string) => (state: RootState) =>
    state.mother.sessionLore[correlationID]

export let selectMotherSessionLoreStatus =
  (correlationID: string) => (state: RootState) =>
    state.mother.sessionLoreStatus[correlationID] || 'uninitialized'

export let selectMotherNodeDetail =
  (correlationID: string, nodeID: string) => (state: RootState) =>
    state.mother.nodeDetails[`${correlationID}:${nodeID}`]

export let selectMotherNodeDetailStatus =
  (correlationID: string, nodeID: string) => (state: RootState) =>
    state.mother.nodeDetailStatus[`${correlationID}:${nodeID}`] ||
    'uninitialized'

// Filtered sessions selector
export let selectFilteredMotherSessions = (state: RootState) => {
  let { sessions } = state.mother
  let { filters } = state.mother

  return sessions.filter((session: MotherSessionSummary) => {
    // Status filter
    if (filters.status && session.status !== filters.status) {
      return false
    }

    // Search filter
    if (filters.search) {
      let searchTerm = filters.search.toLowerCase()
      if (
        !session.correlationID.toLowerCase().includes(searchTerm) &&
        !session.userID.toLowerCase().includes(searchTerm)
      ) {
        return false
      }
    }

    // Date range filter (basic implementation)
    if (filters.dateRange.start || filters.dateRange.end) {
      let sessionDate = new Date(session.createdTime)
      if (
        filters.dateRange.start &&
        sessionDate < new Date(filters.dateRange.start)
      ) {
        return false
      }
      if (
        filters.dateRange.end &&
        sessionDate > new Date(filters.dateRange.end)
      ) {
        return false
      }
    }

    return true
  })
}

export default motherSlice.reducer
